import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getTenantToday } from '../utils/dateUtils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldAlert,
  RefreshCcw,
  Activity,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Zap,
  Layers,
  PieChart,
  Bot,
  Sparkles,
  Info,
  Calendar,
  AlertTriangle,
  Flame,
  BarChart3,
  Dna,
  History,
  Settings,
  MoreVertical,
  Clock,
  Target,
  Building2,
  Landmark,
  Briefcase,
  Shield,
  DollarSign,
  Users,
  Percent
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { cn } from '../lib/utils';
import LoanExplainDrawer, { LoanRiskRow } from './LoanExplainDrawer';

import { DateField } from './shared/DateField';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> =
  ({ label, value, icon, color, subText }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Sleeve Types ==============
type RiskSleeve = 'all' | 'public-fi' | 'private-credit' | 'abf' | 'real-estate' | 'alternatives' | 'insurance';

const RISK_SLEEVES: { id: RiskSleeve; label: string }[] = [
  { id: 'all', label: 'All Assets' },
  { id: 'public-fi', label: 'Public FI' },
  { id: 'private-credit', label: 'Private Credit' },
  { id: 'abf', label: 'Asset-Based Finance' },
  { id: 'real-estate', label: 'Real Estate' },
  { id: 'alternatives', label: 'PE / Alts' },
  { id: 'insurance', label: 'Insurance' },
];

// ============== Interfaces ==============
/** GET /api/credit/abf/board/risk/portfolio/{id}: governed facility headlines + obligor look-through. */
interface AbfRiskFacility {
  facilityId: number;
  facilityCode: string;
  facilityName: string;
  obligorName?: string;
  collateralType?: string;
  status: string;
  currency?: string;
  headline: {
    commitment?: number; exposure?: number; exposureAuthority?: string; borrowingBase?: number;
    borrowingBaseAsOf?: string; availability?: number; availabilityBeforeHolds?: number; availabilityGate?: string;
    /** F-MK-073: this portfolio's share of a co-held facility; exposure and commitment above are its own. */
    holderSharePct?: number; facilityExposure?: number; facilityCommitment?: number; exposureScope?: string;
  };
  covenants?: { tests?: number; breaches?: number; testDate?: string };
  drawGate?: { state?: string };
  openBreaks?: { count?: number; value?: number };
  blockingExceptions?: number;
  /** Item G (V697): the largest obligor carries its master id, LEI and how its identity was established. */
  lookThrough?: { obligor: string; shareOfPoolPct: number; obligorCount: number; obligorMasterId?: number | null; lei?: string | null; matchBasis?: 'LEI' | 'CURATED' | 'NAME' } | null;
}

/** How an obligor's identity was established: LEI-anchored, reviewer-curated, or spelling only. */
const BasisChip: React.FC<{ basis?: 'LEI' | 'CURATED' | 'NAME' }> = ({ basis }) => {
  const b = basis || 'NAME';
  const cls = b === 'LEI' ? 'bg-emerald-500/15 text-emerald-300' : b === 'CURATED' ? 'bg-sky-500/15 text-sky-300' : 'bg-slate-500/20 text-slate-300';
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`} title={b === 'LEI' ? 'Anchored on a Legal Entity Identifier' : b === 'CURATED' ? 'A reviewer merged or aliased this obligor' : 'Matched on spelling only'}>{b === 'LEI' ? 'LEI' : b === 'CURATED' ? 'curated' : 'name'}</span>;
};

interface AbfRisk {
  hasData: boolean;
  businessDate?: string;
  totals?: {
    facilities: number; commitment: number; exposure: number; undrawn: number; borrowingBase: number;
    availability: number; facilitiesInBreach: number; facilitiesHeld: number; blockingExceptions: number;
    openBreaks: number; basis: string; facilityLevel?: string[];
  };
  facilities?: AbfRiskFacility[];
  lookThrough?: {
    topObligors: Array<{ obligor: string; exposure: number; pctOfAbfExposure: number; facilities: number; obligorMasterId?: number | null; lei?: string | null; matchBasis?: 'LEI' | 'CURATED' | 'NAME' }>;
    exposureNotLookedThrough: number;
    basis: string;
  };
}

interface Portfolio {
  id: number;
  portfolioName: string;
}

interface PrivateCreditRisk {
  creditVaR: number;
  spreadDuration: number;
  totalExposure: number;
  topBorrowers: Array<{ name: string; exposure: number; percentage: number }>;
  sectorConcentration: Array<{ sector: string; percentage: number }>;
}

interface PcLlLoanRiskRow {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sponsorName?: string;
  sector?: string;
  facilityType?: string;
  seniority?: string;
  rating?: string;
  fundedExposure: number;
  unfundedCommitment: number;
  exposureAtDefault: number;
  probabilityDefault: number;
  lossGivenDefault: number;
  recoveryRate: number;
  expectedLoss: number;
  expectedLossPctOfFundedExposure: number;
  spreadBps?: number;
  allInRate?: number;
  spreadDuration: number;
  spreadDv01: number;
  baseRateDv01: number;
  defaultStressLoss: number;
  economicCapital: number;
  pikElectionCurrent?: string;
  nonAccrual?: boolean;
  covenantName?: string;
  covenantCushionValue?: number;
  covenantCushionPct?: number;
  covenantStatus?: string;
  covenantTestDate?: string;
  lmeScore?: number;
  lmeSeverity?: string;
  lmeAssessmentStatus?: string;
}

interface PcLlRiskReport {
  tenantId: number;
  portfolioId: number;
  asOfDate: string;
  persisted: boolean;
  snapshotStatus?: string;
  snapshotType?: string;
  snapshotDate?: string;
  requestedAsOfDate?: string;
  error?: string;
  methodology: string;
  summary: {
    positionCount: number;
    totalFundedExposure: number;
    totalUnfundedCommitment: number;
    totalExposureAtDefault: number;
    totalExpectedLoss: number;
    expectedLossPctOfFundedExposure: number;
    weightedAveragePd: number;
    weightedAverageLgd: number;
    weightedAverageRecovery: number;
    spreadDv01: number;
    baseRateDv01: number;
    spreadWidening100bpPnl: number;
    defaultStressLoss: number;
    totalEconomicCapital: number;
    economicCapitalPctOfFundedExposure: number;
    borrowerConcentrationHhi: number;
    exposureByLoanSource: Record<string, number>;
    topSectors: Record<string, number>;
    topSponsors: Record<string, number>;
    topBorrowers: Record<string, number>;
    covenantWarningOrBreachCount: number;
    nonAccrualCount: number;
    pikElectionCount: number;
    approvedHighLmeCount?: number;
  };
  loans: PcLlLoanRiskRow[];
}

interface RealEstateRisk {
  averageLTV: number;
  averageDSCR: number;
  propertyTypeBreakdown?: Array<{ type: string; percentage: number; marketValue: number }>;
  propertyTypeConcentration?: Record<string, number>;
  totalExposure?: number;
  stressTests: Array<{ scenario: string; impact: number; impactFormatted: string }>;
}

interface AlternativesMetrics {
  totalCommitted: number;
  totalCalled: number;
  totalDistributed: number;
  totalNAV: number;
  tvpi: number;
  dpi: number;
  rvpi?: number;
  moic: number;
  commitmentCount?: number;
  jCurveData?: Array<JCurveBucket>;
  vintageYearBreakdown?: Record<string, any>;
  fundPerformance?: Array<{
    fundId?: number;
    fundName: string;
    type: string;
    vintage: number;
    committed?: number;
    called?: number;
    distributed?: number;
    nav?: number;
    tvpi: number;
    dpi: number;
    rvpi?: number;
    moic: number;
  }>;
}

interface JCurveBucket {
  month: string;
  monthEnd?: string;
  cumulativeCalled: number;
  cumulativeDistributed: number;
  reportedNav: number;
  totalValue: number;
  netCashFlow: number;
  unrealizedPnl?: number;
  dpi: number;
  rvpi: number;
  tvpi: number;
}

interface JCurveSummary {
  totalCommitted?: number;
  cumulativeCalled?: number;
  cumulativeDistributed?: number;
  reportedNav?: number;
  totalValue?: number;
  netCashFlow?: number;
  dpi?: number;
  rvpi?: number;
  tvpi?: number;
}

interface JCurveResult {
  scope: string;
  portfolioId?: number;
  groupBy?: string;
  commitmentCount?: number;
  totalCommitted?: number;
  latestNav?: number;
  latestNavDate?: string | null;
  buckets?: JCurveBucket[];
  summary?: JCurveSummary;
  groups?: Array<{
    label: string;
    commitmentCount: number;
    totalCommitted: number;
    latestNav: number;
    latestNavDate?: string | null;
    buckets: JCurveBucket[];
    summary: JCurveSummary;
  }>;
}

type JCurveGroupBy = 'aggregate' | 'vintage' | 'fund';

interface InsuranceRisk {
  naicDistribution: Array<{ naic: number; percentage: number; marketValue: number }>;
  bookYield: number;
  rbcContributions: Array<{ position: string; rbcCharge: number; marketValue: number }>;
  statutoryValue: number;
  marketValue: number;
  statutoryMarketDifference: number;
}

interface RiskData {
  portfolioId: number;
  portfolioName: string;
  asOfDate: string;
  effectivePositionAsOfDate?: string;
  riskSnapshotDate?: string;
  requestedAsOfDate?: string;
  riskSnapshotStatus?: string;
  riskSnapshotType?: string;
  positionCount: number;
  totalMarketValue: number;
  duration: number;
  convexity: number;
  targetDuration: number;
  durationGap: number;
  durationGapStatus: string;
  dv01: number;
  // F-DD-001 cross-asset composition. dv01 above is the composed SIGNED net
  // (P&L per +1bp) on new snapshots; pre-composition snapshots lack these.
  netDv01?: number;
  grossDv01?: number;
  dv01Convention?: string;
  crossAssetStatus?: string;
  crossAsset?: {
    convention: string;
    netDv01: number;
    grossDv01: number;
    rateDirection: 'NET_SHORT_RATES' | 'NET_LONG_RATES' | 'FLAT';
    equityExposure: { optionsDeltaAdjusted: number; futuresBetaAdjusted: number; total: number };
    components: Array<{
      assetClass: string;
      positions: number;
      dv01?: number;
      cs01?: number;
      marketValue?: number;
      duration?: number;
      notional?: number;
      netPv?: number;
      deltaAdjustedExposure?: number;
      vega?: number;
      theta?: number;
      betaAdjustedExposure?: number;
      dv01MissingPositions?: number;
    }>;
    swapDv01Missing?: number;
  };
  var: {
    var1Day95: number;
    var1Day99: number;
    var10Day95: number;
    var10Day99: number;
    yieldVolatility: number;
    varBasis?: string;
  };
  stressScenarios: Array<{
    name: string;
    description: string;
    pnlImpact: number;
    pnlImpactFormatted: string;
    severity: string;
    icon: string;
    isNegative: boolean;
  }>;
  concentration: {
    byAssetClass: Array<{ category: string; percentage: number; marketValue: number }>;
    topIssuers: Array<{ issuer: string; percentage: number; marketValue: number }>;
    topIssuerConcentration: number;
    top5IssuerConcentration: number;
    concentrationRisk: string;
  };
  qualityDistribution: {
    distribution: Array<{ rating: string; percentage: number; color: string }>;
    investmentGradePercent: number;
    highYieldPercent: number;
  };
  maturityDistribution: {
    distribution: Array<{ bucket: string; percentage: number }>;
  };
  keyRiskIndicators: Array<{
    name: string;
    status: string;
    value: string;
    description: string;
  }>;
  privateCreditRisk?: PrivateCreditRisk;
  realEstateRisk?: RealEstateRisk;
  alternativesMetrics?: AlternativesMetrics;
  insuranceRisk?: InsuranceRisk;
}

// ============== Helper ==============
const fmtM = (v: number) => (v == null || isNaN(v as number)) ? '—' : `$${(v / 1000000).toFixed(1)}M`;
const fmtK = (v: number) => `$${(v / 1000).toFixed(1)}K`;
// DV01 is quoted signed and to the dollar (a $1,723/bp book must not render as $2K).
const fmtSignedDollars = (v: number | null | undefined) =>
  v == null || isNaN(v as number) ? '—'
    : `${(v as number) > 0 ? '+' : (v as number) < 0 ? '-' : ''}$${Math.abs(Math.round(v as number)).toLocaleString()}`;
const fmtPctDec = (v: number | null | undefined, digits = 2) =>
  v == null || isNaN(v as number) ? '—' : `${((v as number) * 100).toFixed(digits)}%`;
const fmtBps = (v: number | null | undefined) =>
  v == null || isNaN(v as number) ? '—' : `${Number(v).toFixed(0)} bps`;

// Money formatter for J-curve axes/tooltips — handles billions/millions/thousands
const fmtMoneyAxis = (v: number | null | undefined) => {
  if (v == null || isNaN(v as number)) return '—';
  const n = v as number;
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtRatio = (v: number | null | undefined) =>
  v == null || isNaN(v as number) ? '—' : `${(v as number).toFixed(2)}x`;

const JCURVE_GROUP_COLORS = ['#60a5fa', '#a78bfa', '#34d399', '#fbbf24', '#f472b6', '#fb7185'];

const RiskDashboardScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [riskData, setRiskData] = useState<RiskData | null>(null);
  const [pcLlRiskData, setPcLlRiskData] = useState<PcLlRiskReport | null>(null);
  const [abfRisk, setAbfRisk] = useState<AbfRisk | null>(null);
  const navigate = useNavigate();
  const [selectedRiskLoan, setSelectedRiskLoan] = useState<PcLlLoanRiskRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asOfDate, setAsOfDate] = useState(getTenantToday());
  const [aiCommentary, setAiCommentary] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeSleeve, setActiveSleeve] = useState<RiskSleeve>('all');
  const [jCurveGroupBy, setJCurveGroupBy] = useState<JCurveGroupBy>('aggregate');
  const [jCurveData, setJCurveData] = useState<JCurveResult | null>(null);
  const [jCurveLoading, setJCurveLoading] = useState(false);

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const response = await axios.get('/api/dimensions/portfolios');
        const pList = response.data?.content || response.data || [];
        setPortfolios(Array.isArray(pList) ? pList : []);
        if (pList.length > 0) setSelectedPortfolioId(pList[0].id);
      } catch (err) {
        console.error('Error fetching portfolios:', err);
      }
    };
    fetchPortfolios();
  }, []);

  const fetchRiskData = useCallback(async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/risk/portfolio/${selectedPortfolioId}`, {
        params: { asOfDate }
      });
      if (response.data?.error) {
        setRiskData(null);
        setError(response.data.error);
      } else {
        setRiskData(response.data);
      }
      try {
        const pcLlResponse = await axios.get(`/api/credit/pc-ll-analytics/risk/portfolio/${selectedPortfolioId}`, {
          params: { asOfDate }
        });
        setPcLlRiskData(pcLlResponse.data);
      } catch (pcLlErr) {
        console.warn('Native PC/LL risk endpoint unavailable:', pcLlErr);
        setPcLlRiskData(null);
      }
      try {
        // ABF facilities are read from their governed facility state, not a loan model.
        const abfResponse = await axios.get(`/api/credit/abf/board/risk/portfolio/${selectedPortfolioId}`);
        setAbfRisk(abfResponse.data);
      } catch (abfErr) {
        // Not licensed for ABF, or no access: the tab says so rather than showing zeros.
        console.warn('ABF risk endpoint unavailable:', abfErr);
        setAbfRisk(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch risk analytics');
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId, asOfDate]);

  useEffect(() => {
    fetchRiskData();
  }, [fetchRiskData]);

  // On-demand producer: POST the sanctioned refresh endpoint, then re-read.
  // This is how a fresh portfolio gets its first snapshot before the EOD
  // batch or a posting event produces one — the GETs themselves never calculate.
  const handleRecalculateRisk = useCallback(async () => {
    if (!selectedPortfolioId || recalculating) return;
    setRecalculating(true);
    try {
      // #68: sleeve-aware producer. The PC/LL surface has its OWN snapshot
      // store and refresh endpoint — the main endpoint never touches it, so
      // recalculating from the Private Credit sleeve must hit both.
      if (activeSleeve === 'private-credit') {
        await axios.post(`/api/credit/pc-ll-analytics/risk/portfolio/${selectedPortfolioId}/refresh`, null, {
          params: { asOfDate }
        });
      }
      await axios.post(`/api/risk/portfolio/${selectedPortfolioId}/refresh`, null, {
        params: { asOfDate }
      });
      await fetchRiskData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to recalculate risk snapshot');
    } finally {
      setRecalculating(false);
    }
  }, [selectedPortfolioId, asOfDate, recalculating, fetchRiskData, activeSleeve]);

  // Fetch J-curve only when the PE/Alts sleeve is active. The endpoint does
  // real ledger math (capital calls + distributions + reported NAV) so we
  // don't pre-fetch it for sleeves that won't render it.
  useEffect(() => {
    if (activeSleeve !== 'alternatives' || !selectedPortfolioId) return;
    setJCurveLoading(true);
    const params = jCurveGroupBy === 'aggregate' ? {} : { groupBy: jCurveGroupBy };
    axios
      .get(`/api/alternatives/phase3/portfolios/${selectedPortfolioId}/j-curve`, { params })
      .then((r) => setJCurveData(r.data))
      .catch(() => setJCurveData(null))
      .finally(() => setJCurveLoading(false));
  }, [activeSleeve, selectedPortfolioId, jCurveGroupBy]);

  const generateAiCommentary = async () => {
    if (!riskData) return;
    setAiLoading(true);
    try {
      const metrics = {
        portfolio_duration: riskData.duration || 0,
        VaR_95: riskData.var?.var1Day95 || 0,
        total_market_value: riskData.totalMarketValue || 0,
        dv01: riskData.dv01 || 0
      };
      const response = await axios.post('/api/ai/risk/commentary', metrics);
      setAiCommentary(response.data);
    } catch (error) {
      console.error('Error generating AI commentary:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const getDonutGradient = () => {
    if (!riskData?.qualityDistribution?.distribution) return 'conic-gradient(#94a3b8 0deg 360deg)';
    let cumulative = 0;
    const segments = riskData.qualityDistribution.distribution
      .filter(d => d.percentage > 0)
      .map(d => {
        const start = cumulative;
        cumulative += d.percentage;
        return `${d.color} ${start * 3.6}deg ${cumulative * 3.6}deg`;
      });
    return `conic-gradient(${segments.join(', ')})`;
  };

  // ============== Sleeve-specific Renderers ==============

  const renderAllAssetsSummary = () => {
    const pcExposure = riskData?.privateCreditRisk?.totalExposure;
    const reBreakdown = riskData?.realEstateRisk?.propertyTypeBreakdown || riskData?.realEstateRisk?.propertyTypeConcentration;
    const reValue = reBreakdown ? (Array.isArray(reBreakdown) ? reBreakdown.reduce((sum: number, p: any) => sum + (p.marketValue || 0), 0) : Object.values(reBreakdown).reduce((sum: number, v: any) => sum + Number(v || 0), 0)) : 0;
    const altsNAV = riskData?.alternativesMetrics?.totalNAV;
    const insRbc = riskData?.insuranceRisk?.rbcContributions?.reduce((sum, r) => sum + (r.rbcCharge || 0), 0);
    const abfExposure = abfRisk?.hasData ? abfRisk.totals?.exposure : undefined;
    const hasAny = pcExposure || abfExposure || reValue || altsNAV || insRbc;
    if (!hasAny) return null;

    const topIssuers = riskData?.concentration?.topIssuers || [];
    return (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {pcExposure != null && (
          <StatCard label="Private Credit" value={fmtM(pcExposure)} icon={<Briefcase className="w-5 h-5" />} color="#a855f7" subText="Total exposure" />
        )}
        {abfExposure != null && (
          <StatCard label="Asset-Based Finance" value={fmtM(abfExposure)} icon={<Layers className="w-5 h-5" />} color="#2dd4bf" subText={`${abfRisk?.totals?.facilities} facilities · official IBOR`} />
        )}
        {reValue != null && (
          <StatCard label="Real Estate" value={fmtM(reValue)} icon={<Building2 className="w-5 h-5" />} color="#f97316" subText="Total property value" />
        )}
        {altsNAV != null && (
          <StatCard label="PE / Alts NAV" value={fmtM(altsNAV)} icon={<Landmark className="w-5 h-5" />} color="#6366f1" subText="Net asset value" />
        )}
        {insRbc != null && (
          <StatCard label="Insurance RBC" value={`${(insRbc / 1000000).toFixed(2)}x`} icon={<Shield className="w-5 h-5" />} color="#14b8a6" subText="RBC contribution" />
        )}
      </div>
      {topIssuers.length > 0 && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-sky-400" />
            <h3 className="text-lg font-bold text-white">Top issuers and obligors</h3>
          </div>
          <p className="text-[11px] text-slate-500 mb-4">
            Net exposure by issuer across the portfolio. ABF facilities are looked through to the obligors in their latest released tape instead of counting as one borrower.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {topIssuers.map(i => (
              <div key={i.issuer} className="space-y-1.5">
                <div className="flex justify-between text-xs font-bold">
                  <span className="text-slate-300 truncate pr-3">{i.issuer}</span>
                  <span className="font-mono text-white">{fmtM(Number(i.marketValue))} <span className="text-slate-500">{Number(i.percentage).toFixed(2)}%</span></span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500" style={{ width: `${Math.min(Number(i.percentage), 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
      </>
    );
  };

  const renderPrivateCreditSection = () => {
    if (pcLlRiskData?.snapshotStatus === 'MISSING_SNAPSHOT') {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-amber-200 text-sm border-2 border-dashed border-amber-400/20 rounded-2xl bg-amber-500/5 flex flex-col items-center gap-4">
            <span>{pcLlRiskData.error || 'PC/LL risk snapshot is not available for this date.'}</span>
            {/* #68: sanctioned producer path — reads never calculate. */}
            <button
              onClick={handleRecalculateRisk}
              disabled={recalculating}
              className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
            >
              {recalculating ? 'Recalculating…' : 'Recalculate'}
            </button>
          </div>
        </GlassCard>
      );
    }

    if (pcLlRiskData?.summary && pcLlRiskData.loans?.length > 0) {
      const summary = pcLlRiskData.summary;
      const exposureRows = (items: Record<string, number> | undefined, total: number) =>
        Object.entries(items || {})
          .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
          .slice(0, 8)
          .map(([name, exposure]) => ({
            name,
            exposure: Number(exposure || 0),
            pct: total > 0 ? Number(exposure || 0) / total : 0
          }));

      const topBorrowers = exposureRows(summary.topBorrowers, summary.totalFundedExposure);
      const topSectors = exposureRows(summary.topSectors, summary.totalFundedExposure);
      const sourceRows = exposureRows(summary.exposureByLoanSource, summary.totalFundedExposure);

      return (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard label="Expected Loss" value={fmtK(summary.totalExpectedLoss)} icon={<ShieldAlert className="w-5 h-5" />} color="#a855f7" subText={fmtPctDec(summary.expectedLossPctOfFundedExposure) + ' of funded exposure'} />
            <StatCard label="Exposure at Default" value={fmtM(summary.totalExposureAtDefault)} icon={<Target className="w-5 h-5" />} color="#14b8a6" subText={`${fmtM(summary.totalFundedExposure)} funded / ${fmtM(summary.totalUnfundedCommitment)} unfunded`} />
            <StatCard label="Spread DV01" value={fmtK(summary.spreadDv01)} icon={<TrendingDown className="w-5 h-5" />} color="#ec4899" subText={`100bp spread shock: ${fmtM(summary.spreadWidening100bpPnl)}`} />
            <StatCard label="Economic Capital" value={fmtM(summary.totalEconomicCapital)} icon={<Activity className="w-5 h-5" />} color="#f59e0b" subText={`${fmtPctDec(summary.economicCapitalPctOfFundedExposure)} of funded / PD-LGD ${fmtPctDec(summary.weightedAveragePd, 1)}-${fmtPctDec(summary.weightedAverageLgd, 0)}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <GlassCard className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <Users className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">Top Borrowers</h3>
              </div>
              <div className="space-y-3">
                {topBorrowers.map((b) => (
                  <div key={b.name} className="space-y-1.5">
                    <div className="flex justify-between text-xs font-bold">
                      <span className="text-slate-300 truncate pr-3">{b.name}</span>
                      <span className="font-mono text-white">{fmtM(b.exposure)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500" style={{ width: `${Math.min(b.pct * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <BarChart3 className="w-5 h-5 text-pink-400" />
                <h3 className="text-lg font-bold text-white">Sector Risk</h3>
              </div>
              <div className="space-y-3">
                {topSectors.map((s) => (
                  <div key={s.name} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                    <span className="text-slate-300">{s.name}</span>
                    <span className="font-mono text-white">{fmtPctDec(s.pct, 1)}</span>
                  </div>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <Briefcase className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">Pilot Controls</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Borrower HHI</span>
                  <span className="font-mono text-white">{summary.borrowerConcentrationHhi?.toFixed(4)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Covenant exceptions</span>
                  <span className="font-mono text-white">{summary.covenantWarningOrBreachCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Non-accrual names</span>
                  <span className="font-mono text-white">{summary.nonAccrualCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">PIK elections</span>
                  <span className="font-mono text-white">{summary.pikElectionCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Approved high LME</span>
                  <span className={cn(
                    "font-mono",
                    Number(summary.approvedHighLmeCount || 0) > 0 ? "text-amber-400" : "text-white"
                  )}>{summary.approvedHighLmeCount || 0}</span>
                </div>
                {sourceRows.map((row) => (
                  <div key={row.name} className="flex justify-between text-sm">
                    <span className="text-slate-400">{row.name.replace('_', ' ')}</span>
                    <span className="font-mono text-white">{fmtM(row.exposure)}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </div>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Layers className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">Loan-Level Expected Loss</h3>
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                persisted to fact_credit_risk: {pcLlRiskData.persisted ? 'yes' : 'no'}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Borrower</th>
                    <th className="pb-3 px-2">Type</th>
                    <th className="pb-3 px-2">Rating</th>
                    <th className="pb-3 px-2 text-right">Funded</th>
                    <th className="pb-3 px-2 text-right">Unfunded</th>
                    <th className="pb-3 px-2 text-right">EAD</th>
                    <th className="pb-3 px-2 text-right">PD</th>
                    <th className="pb-3 px-2 text-right">LGD</th>
                    <th className="pb-3 px-2 text-right">EL</th>
                    <th className="pb-3 px-2 text-right">Capital</th>
                    <th className="pb-3 px-2 text-right">Spread</th>
                    <th className="pb-3 px-2 text-right">Covenant</th>
                    <th className="pb-3 px-2 text-right">LME</th>
                    <th className="pb-3 px-2 text-right"></th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {pcLlRiskData.loans.slice(0, 25).map((loan) => (
                    <tr
                      key={`${loan.loanSource}-${loan.loanId}`}
                      onClick={() => setSelectedRiskLoan(loan)}
                      className="group border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-2">
                        <div className="font-bold text-white">{loan.borrowerName || loan.loanName}</div>
                        <div className="text-[11px] text-slate-500">{loan.sponsorName || loan.sector || '—'}</div>
                      </td>
                      <td className="py-3 px-2 text-slate-300">{loan.loanSource === 'PRIVATE_CREDIT' ? 'PC' : 'LL'}</td>
                      <td className="py-3 px-2 font-mono text-slate-300">{loan.rating || 'NR'}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtM(loan.fundedExposure)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtM(loan.unfundedCommitment)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtM(loan.exposureAtDefault)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtPctDec(loan.probabilityDefault)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtPctDec(loan.lossGivenDefault)}</td>
                      <td className="py-3 px-2 text-right font-mono text-white">{fmtK(loan.expectedLoss)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtK(loan.economicCapital)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtBps(loan.spreadBps)}</td>
                      <td className="py-3 px-2 text-right">
                        <span className={cn("font-mono text-xs", loan.covenantStatus && loan.covenantStatus !== 'PASSED' ? "text-amber-300" : "text-slate-400")}>
                          {loan.covenantStatus || '—'}
                        </span>
                      </td>
                      <td className={cn(
                        "py-3 px-2 text-right font-mono text-xs",
                        loan.lmeSeverity === 'HIGH' || loan.lmeSeverity === 'CRITICAL'
                          ? "text-amber-400"
                          : "text-slate-400"
                      )}>
                        {loan.lmeAssessmentStatus === 'APPROVED'
                          ? `${loan.lmeSeverity || 'Scored'} ${loan.lmeScore ?? ''}`.trim()
                          : '—'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 group-hover:text-blue-400 transition-colors">
                          Explain
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </div>
      );
    }

    const pc = riskData?.privateCreditRisk;
    if (!pc) return (
      <GlassCard className="p-6">
        <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
          No private credit risk data available for this portfolio.
        </div>
      </GlassCard>
    );

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard label="Credit VaR" value={fmtK(pc.creditVaR)} icon={<AlertTriangle className="w-5 h-5" />} color="#a855f7" subText="99% 1-day" />
          <StatCard label="Spread Duration" value={`${(pc.spreadDuration || 0).toFixed(2)}y`} icon={<Clock className="w-5 h-5" />} color="#ec4899" subText="Weighted avg spread dur" />
          <StatCard label="Total Exposure" value={fmtM(pc.totalExposure)} icon={<DollarSign className="w-5 h-5" />} color="#14b8a6" subText="Gross credit exposure" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-bold text-white">Top Borrowers</h3>
            </div>
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="pb-3 px-2">Borrower</th>
                  <th className="pb-3 px-2">Exposure</th>
                  <th className="pb-3 px-2 text-right">%</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {pc.topBorrowers?.map((b, i) => (
                  <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                    <td className="py-3 px-2 font-bold text-white">{b.name}</td>
                    <td className="py-3 px-2 font-mono text-slate-300">{fmtM(b.exposure)}</td>
                    <td className="py-3 px-2 text-right font-mono text-slate-400">{(b.percentage || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-pink-400" />
              <h3 className="text-lg font-bold text-white">Sector Concentration</h3>
            </div>
            <div className="space-y-4">
              {pc.sectorConcentration && (Array.isArray(pc.sectorConcentration)
                ? pc.sectorConcentration
                : Object.entries(pc.sectorConcentration).map(([sector, value]) => ({
                    sector,
                    percentage: pc.totalExposure > 0 ? ((Number(value) / pc.totalExposure) * 100) : 0
                  }))
              ).map((s: any, i: number) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between items-end text-xs font-bold">
                    <span className="text-slate-400 uppercase tracking-widest">{s.sector}</span>
                    <span className="text-white">{Number(s.percentage || 0).toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${s.percentage || 0}%` }}
                      className="h-full bg-purple-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </div>
    );
  };

  const renderAbfSection = () => {
    if (!abfRisk) {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-slate-400 text-sm">
            Asset-based finance risk is not available here. The tenant may not be licensed for ABF, or the ABF service did not answer.
          </div>
        </GlassCard>
      );
    }
    if (!abfRisk.hasData || !abfRisk.totals) {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-slate-400 text-sm">This portfolio holds no active ABF facilities.</div>
        </GlassCard>
      );
    }
    const t = abfRisk.totals;
    const look = abfRisk.lookThrough;
    const facilities = abfRisk.facilities || [];
    return (
      <div className="space-y-8">
        <p className="text-xs text-slate-500">
          Governed facility state as of the business date {abfRisk.businessDate}. {t.basis} The date picker above does not apply to this sleeve.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard label="ABF Exposure" value={fmtM(t.exposure)} icon={<Layers className="w-5 h-5" />} color="#2dd4bf" subText={`${fmtM(t.commitment)} committed · ${fmtM(t.undrawn)} undrawn`} />
          <StatCard label="Approved Borrowing Base" value={fmtM(t.borrowingBase)} icon={<Shield className="w-5 h-5" />} color="#60a5fa" subText={`${t.facilities} facilities · whole facility`} />
          <StatCard label="Availability" value={fmtM(t.availability)} icon={<DollarSign className="w-5 h-5" />} color="#34d399" subText="After holds · whole facility" />
          <StatCard label="Breaches / Holds" value={`${t.facilitiesInBreach} / ${t.facilitiesHeld}`} icon={<AlertTriangle className="w-5 h-5" />} color={t.facilitiesInBreach || t.facilitiesHeld ? '#f59e0b' : '#64748b'} subText={`${t.blockingExceptions} blocking exceptions · ${t.openBreaks} open breaks`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <GlassCard className="p-6 lg:col-span-2 overflow-x-auto">
            <div className="flex items-center gap-3 mb-5">
              <Layers className="w-5 h-5 text-teal-400" />
              <h3 className="text-lg font-bold text-white">Facilities</h3>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 text-left uppercase tracking-wider">
                  <th className="py-2 pr-3">Facility</th>
                  <th className="py-2 pr-3 text-right">Exposure</th>
                  <th className="py-2 pr-3 text-right">Base</th>
                  <th className="py-2 pr-3 text-right">Availability</th>
                  <th className="py-2 pr-3 text-right">Covenants</th>
                  <th className="py-2 pr-3 text-right">Breaks</th>
                  <th className="py-2 pr-3 text-right">Largest obligor</th>
                  <th className="py-2 pr-3 text-right">LEI / identifier</th>
                  <th className="py-2 text-right">Basis</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map(f => {
                  const breaches = f.covenants?.breaches ?? 0;
                  const heldGate = f.drawGate?.state === 'HELD';
                  return (
                    <tr key={f.facilityId} className="border-t border-white/5 text-slate-300">
                      <td className="py-2 pr-3">
                        <button type="button" className="text-left hover:text-teal-300"
                          onClick={() => navigate(`/portfolio-management/credit/abf/facilities/${f.facilityId}`)}>
                          <div className="font-bold text-white">{f.facilityCode}</div>
                          <div className="text-slate-500">{f.facilityName}</div>
                        </button>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {fmtM(Number(f.headline.exposure ?? 0))}
                        {f.headline.holderSharePct != null && Number(f.headline.holderSharePct) < 100 && (
                          <div className="font-sans text-[10px] text-slate-500">
                            {Number(f.headline.holderSharePct).toFixed(2)}% share of {fmtM(Number(f.headline.facilityExposure ?? 0))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{f.headline.borrowingBase != null ? fmtM(Number(f.headline.borrowingBase)) : '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono" title={f.headline.availabilityGate || undefined}>
                        {f.headline.availability != null ? fmtM(Number(f.headline.availability)) : '—'}
                        {heldGate && <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">HELD</span>}
                        {f.headline.availabilityGate && Number(f.headline.availability ?? 0) === 0 && (
                          <div className="font-sans text-[10px] text-amber-300/80 max-w-[16rem] ml-auto whitespace-normal">
                            {f.headline.availabilityGate}
                            {Number(f.headline.availabilityBeforeHolds ?? 0) > 0 && ` · ${fmtM(Number(f.headline.availabilityBeforeHolds))} before holds`}
                          </div>
                        )}
                      </td>
                      <td className={`py-2 pr-3 text-right ${breaches > 0 ? 'text-amber-300 font-bold' : ''}`}>
                        {f.covenants?.tests ? `${breaches} of ${f.covenants.tests} in breach` : 'Not tested'}
                      </td>
                      <td className="py-2 pr-3 text-right">{f.openBreaks?.count ?? 0}</td>
                      <td className="py-2 pr-3 text-right">
                        {f.lookThrough
                          ? <>{f.lookThrough.obligor} <span className="text-slate-500">{Number(f.lookThrough.shareOfPoolPct).toFixed(2)}% of {f.lookThrough.obligorCount}</span></>
                          : <span className="text-slate-500">No released tape</span>}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-[11px]">
                        {f.lookThrough ? (f.lookThrough.lei || (f.lookThrough.obligorMasterId != null ? `master #${f.lookThrough.obligorMasterId}` : '—')) : '—'}
                      </td>
                      <td className="py-2 text-right">
                        {f.lookThrough ? <BasisChip basis={f.lookThrough.matchBasis} /> : <span className="text-slate-500">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-teal-400" />
              <h3 className="text-lg font-bold text-white">Obligor look-through</h3>
            </div>
            <p className="text-[11px] text-slate-500 mb-4">{look?.basis}</p>
            <div className="space-y-3">
              {(look?.topObligors || []).map(o => (
                <div key={o.obligorMasterId != null ? `m-${o.obligorMasterId}` : o.obligor} className="space-y-1.5">
                  <div className="flex justify-between text-xs font-bold">
                    <span className="text-slate-300 truncate pr-3">
                      {o.obligor}{o.facilities > 1 ? ` · ${o.facilities} facilities` : ''}
                      {o.lei && <span className="ml-2 font-mono font-normal text-[10px] text-slate-500">{o.lei}</span>}
                      <span className="ml-2"><BasisChip basis={o.matchBasis} /></span>
                    </span>
                    <span className="font-mono text-white">{fmtM(Number(o.exposure))} <span className="text-slate-500">{Number(o.pctOfAbfExposure).toFixed(2)}%</span></span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500" style={{ width: `${Math.min(Number(o.pctOfAbfExposure), 100)}%` }} />
                  </div>
                </div>
              ))}
              {(look?.topObligors || []).length === 0 && <div className="text-xs text-slate-500">No facility has a released tape yet.</div>}
            </div>
            {Number(look?.exposureNotLookedThrough || 0) > 0 && (
              <p className="mt-4 text-[11px] text-amber-300/80">{fmtM(Number(look?.exposureNotLookedThrough))} of exposure is not looked through (no released tape).</p>
            )}
          </GlassCard>
        </div>
      </div>
    );
  };

  const renderRealEstateSection = () => {
    const re = riskData?.realEstateRisk;
    if (!re) return (
      <GlassCard className="p-6">
        <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
          No real estate risk data available for this portfolio.
        </div>
      </GlassCard>
    );

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard label="Average LTV" value={`${(re.averageLTV || 0).toFixed(1)}%`} icon={<Percent className="w-5 h-5" />} color="#f97316" subText="Loan-to-value ratio" />
          <StatCard label="Average DSCR" value={`${(re.averageDSCR || 0).toFixed(2)}x`} icon={<Shield className="w-5 h-5" />} color="#10b981" subText="Debt service coverage ratio" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-5 h-5 text-orange-400" />
              <h3 className="text-lg font-bold text-white">Property Type Breakdown</h3>
            </div>
            <div className="space-y-4">
              {(() => {
                const raw = re.propertyTypeBreakdown || (re as any).propertyTypeConcentration || {};
                const arr = Array.isArray(raw) ? raw : Object.entries(raw).map(([type, value]) => ({
                  type, marketValue: Number(value), percentage: (re.totalExposure || 1) > 0 ? (Number(value) / (re.totalExposure || 1)) * 100 : 0
                }));
                return arr;
              })().map((p: any, i: number) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between items-end text-xs font-bold">
                    <span className="text-slate-400 uppercase tracking-widest">{p.type}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-slate-500 font-mono">{fmtM(p.marketValue)}</span>
                      <span className="text-white">{Number(p.percentage || 0).toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${p.percentage || 0}%` }}
                      className="h-full bg-orange-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Flame className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-bold text-white">RE Stress Tests</h3>
            </div>
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="pb-3 px-2">Scenario</th>
                  <th className="pb-3 px-2 text-right">PnL Impact</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {re.stressTests?.map((st, i) => (
                  <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                    <td className="py-3 px-2 font-bold text-white">{st.scenario}</td>
                    <td className={cn("py-3 px-2 text-right font-mono font-bold", st.impact < 0 ? "text-red-400" : "text-emerald-400")}>
                      {st.impactFormatted}
                    </td>
                  </tr>
                ))}
                {(!re.stressTests || re.stressTests.length === 0) && (
                  <>
                    {[
                      { scenario: 'Cap Rate +50bp', impact: -3.2 },
                      { scenario: 'Cap Rate +100bp', impact: -6.1 },
                      { scenario: 'Vacancy +5%', impact: -2.8 },
                      { scenario: 'Vacancy +10%', impact: -5.5 },
                    ].map((st, i) => (
                      <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-3 px-2 font-bold text-white">{st.scenario}</td>
                        <td className="py-3 px-2 text-right font-mono font-bold text-slate-600">N/A</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </GlassCard>
        </div>
      </div>
    );
  };

  const renderAlternativesSection = () => {
    const alts = riskData?.alternativesMetrics;
    const jc = jCurveData;

    // Empty state — no commitments at all on this portfolio.
    const commitmentCount = jc?.commitmentCount ?? alts?.commitmentCount ?? 0;
    const hasCommitments = commitmentCount > 0 || (alts && (alts.totalCommitted || 0) > 0);
    if (!hasCommitments && !jCurveLoading) {
      return (
        <GlassCard className="p-6">
          <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
            No PE / Alts commitments found for this portfolio.
          </div>
        </GlassCard>
      );
    }

    // KPI strip values: prefer the j-curve summary (it matches the chart) but
    // fall back to riskData.alternativesMetrics so values render before the
    // j-curve fetch completes.
    const summary = jc?.summary;
    const committed = jc?.totalCommitted ?? alts?.totalCommitted ?? 0;
    const called = (summary?.cumulativeCalled as number | undefined) ?? alts?.totalCalled ?? 0;
    const distributed = (summary?.cumulativeDistributed as number | undefined) ?? alts?.totalDistributed ?? 0;
    const nav = (summary?.reportedNav as number | undefined) ?? alts?.totalNAV ?? 0;
    const tvpi = (summary?.tvpi as number | undefined) ?? alts?.tvpi ?? 0;
    const dpi = (summary?.dpi as number | undefined) ?? alts?.dpi ?? 0;

    // Build chart-ready rows. When grouped, merge per-month rows across groups
    // so each group shows as its own colored series on the same x-axis.
    let chartData: any[] = [];
    let groupNames: string[] = [];
    if (jc?.groups && jc.groups.length > 0) {
      const monthMap = new Map<string, any>();
      jc.groups.forEach((g) => {
        groupNames.push(g.label);
        (g.buckets || []).forEach((b) => {
          const row = monthMap.get(b.month) || { month: b.month };
          row[`${g.label} • Called`] = b.cumulativeCalled;
          row[`${g.label} • TotalValue`] = b.totalValue;
          monthMap.set(b.month, row);
        });
      });
      chartData = Array.from(monthMap.values()).sort((a, b) => a.month.localeCompare(b.month));
    } else {
      chartData = (jc?.buckets || []).map((b) => ({
        month: b.month,
        Called: b.cumulativeCalled,
        Distributed: b.cumulativeDistributed,
        NAV: b.reportedNav,
        TotalValue: b.totalValue,
        NetCashFlow: b.netCashFlow,
      }));
    }

    return (
      <div className="space-y-6">
        {/* Header row: title + groupBy toggle */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">PE / Alternatives</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {commitmentCount} commitment{commitmentCount === 1 ? '' : 's'}
              {jc?.latestNavDate ? ` • Latest NAV applied from ${jc.latestNavDate}` : ''}
            </p>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
              Group by
            </label>
            <div className="flex bg-slate-900 border border-white/10 rounded-lg overflow-hidden">
              {(['aggregate', 'vintage', 'fund'] as JCurveGroupBy[]).map((g) => (
                <button
                  key={g}
                  onClick={() => setJCurveGroupBy(g)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-semibold transition-colors',
                    jCurveGroupBy === g
                      ? 'bg-indigo-600/30 text-indigo-200'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  )}
                >
                  {g === 'aggregate' ? 'Aggregate' : g === 'vintage' ? 'Vintage' : 'Fund'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* PE-specific KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Committed" value={fmtMoneyAxis(committed)} icon={<Target className="w-5 h-5" />} color="#6366f1" />
          <StatCard label="Called" value={fmtMoneyAxis(called)} icon={<DollarSign className="w-5 h-5" />} color="#8b5cf6" />
          <StatCard label="Distributed" value={fmtMoneyAxis(distributed)} icon={<TrendingUp className="w-5 h-5" />} color="#10b981" subText="Net of recalls" />
          <StatCard label="Reported NAV" value={fmtMoneyAxis(nav)} icon={<Activity className="w-5 h-5" />} color="#3b82f6" />
          <GlassCard className="p-4 text-center">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">DPI</div>
            <div className={cn('text-2xl font-bold', dpi >= 1 ? 'text-emerald-400' : 'text-white')}>{fmtRatio(dpi)}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Distributed / Called</div>
          </GlassCard>
          <GlassCard className="p-4 text-center">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">TVPI</div>
            <div className={cn('text-2xl font-bold', tvpi >= 1 ? 'text-emerald-400' : 'text-rose-300')}>{fmtRatio(tvpi)}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">(NAV + Dist) / Called</div>
          </GlassCard>
        </div>

        {/* J-curve chart */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {summary && (summary.netCashFlow ?? 0) < 0 ? (
                <TrendingDown className="w-5 h-5 text-amber-400" />
              ) : (
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              )}
              <h3 className="text-base font-semibold text-white">J-curve trajectory</h3>
            </div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">
              Cumulative by month-end
            </div>
          </div>
          {jCurveLoading ? (
            <div className="py-16 text-center text-slate-500 italic text-sm">
              <Activity className="w-4 h-4 inline mr-2 animate-pulse" />
              Loading j-curve…
            </div>
          ) : chartData.length === 0 ? (
            <div className="py-16 text-center text-slate-500 italic text-sm">
              No capital activity yet — chart will populate once calls or distributions are funded.
            </div>
          ) : (
            <div style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => fmtMoneyAxis(v)} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                  <ChartTooltip
                    contentStyle={{
                      background: '#0f172a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: any) => fmtMoneyAxis(v as number)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {groupNames.length === 0 ? (
                    <>
                      <Line type="monotone" dataKey="Called" stroke="#f87171" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Distributed" stroke="#34d399" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="NAV" stroke="#a78bfa" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                      <Line type="monotone" dataKey="TotalValue" stroke="#60a5fa" strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="NetCashFlow" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="2 4" dot={false} />
                    </>
                  ) : (
                    groupNames.flatMap((name, i) => [
                      <Line
                        key={`${name}-called`}
                        type="monotone"
                        dataKey={`${name} • Called`}
                        stroke={JCURVE_GROUP_COLORS[i % JCURVE_GROUP_COLORS.length]}
                        strokeWidth={1.5}
                        strokeDasharray="2 4"
                        dot={false}
                      />,
                      <Line
                        key={`${name}-tv`}
                        type="monotone"
                        dataKey={`${name} • TotalValue`}
                        stroke={JCURVE_GROUP_COLORS[i % JCURVE_GROUP_COLORS.length]}
                        strokeWidth={2.5}
                        dot={false}
                      />,
                    ])
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </GlassCard>

        {/* Per-group summary table when grouped */}
        {jc?.groups && jc.groups.length > 0 && (
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5 text-amber-400" />
              <h3 className="text-base font-semibold text-white">
                By {jCurveGroupBy} ({jc.groups.length} group{jc.groups.length === 1 ? '' : 's'})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Group</th>
                    <th className="pb-3 px-2 text-right">Commitments</th>
                    <th className="pb-3 px-2 text-right">Committed</th>
                    <th className="pb-3 px-2 text-right">Called</th>
                    <th className="pb-3 px-2 text-right">Distributed</th>
                    <th className="pb-3 px-2 text-right">NAV</th>
                    <th className="pb-3 px-2 text-right">DPI</th>
                    <th className="pb-3 px-2 text-right">TVPI</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {jc.groups.map((g) => (
                    <tr key={g.label} className="border-b border-white/5 hover:bg-white/5 transition-colors last:border-0">
                      <td className="py-3 px-2 font-bold text-white">{g.label}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{g.commitmentCount}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtMoneyAxis(g.totalCommitted)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtMoneyAxis(g.summary?.cumulativeCalled)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtMoneyAxis(g.summary?.cumulativeDistributed)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtMoneyAxis(g.summary?.reportedNav)}</td>
                      <td className="py-3 px-2 text-right font-mono text-white">{fmtRatio(g.summary?.dpi)}</td>
                      <td className="py-3 px-2 text-right font-mono text-white">{fmtRatio(g.summary?.tvpi)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}

        {/*
         * Per-fund performance (TVPI/DPI/MOIC) lives under Performance
         * Attribution, not here. Risk Surveillance stays focused on exposure
         * and pacing — KPI strip + J-curve trajectory only.
         */}
      </div>
    );
  };

  const renderInsuranceSection = () => {
    const ins = riskData?.insuranceRisk;
    if (!ins) return (
      <GlassCard className="p-6">
        <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
          No insurance risk data available for this portfolio.
        </div>
      </GlassCard>
    );

    const NAIC_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#f97316', '#ef4444', '#dc2626'];

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {ins.naicDistribution?.map((n, i) => (
            <GlassCard key={i} className="p-4 text-center border-t-4" style={{ borderTopColor: NAIC_COLORS[i] || '#94a3b8' }}>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">NAIC {n.naic}</div>
              <div className="text-2xl font-bold text-white">{(n.percentage || 0).toFixed(1)}%</div>
              <div className="text-[10px] text-slate-500 mt-1 font-mono">{fmtM(n.marketValue)}</div>
            </GlassCard>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard label="Book Yield" value={`${(ins.bookYield || 0).toFixed(2)}%`} icon={<Percent className="w-5 h-5" />} color="#10b981" subText="Wt avg coupon / amortized cost" />
          <StatCard label="Statutory Value" value={fmtM(ins.statutoryValue)} icon={<Landmark className="w-5 h-5" />} color="#3b82f6" subText="Total SAP value" />
          <StatCard label="Market Value" value={fmtM(ins.marketValue)} icon={<Activity className="w-5 h-5" />} color="#8b5cf6" subText={`Diff: ${fmtM(ins.statutoryMarketDifference)}`} />
        </div>

        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-teal-400" />
            <h3 className="text-lg font-bold text-white">RBC Contribution - Top Positions</h3>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                <th className="pb-3 px-2">Position</th>
                <th className="pb-3 px-2">Market Value</th>
                <th className="pb-3 px-2 text-right">RBC Charge</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {ins.rbcContributions?.map((r, i) => (
                <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                  <td className="py-3 px-2 font-bold text-white">{r.position}</td>
                  <td className="py-3 px-2 font-mono text-slate-300">{fmtM(r.marketValue)}</td>
                  <td className="py-3 px-2 text-right font-mono text-amber-400">{fmtK(r.rbcCharge)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      </div>
    );
  };

  const renderExistingFIContent = () => (
    <>
      {/* F-DD-001: per-class sensitivity cards from the cross-asset composition.
          Only rendered when the snapshot carries the composed block (older
          bond-only snapshots simply don't show it). */}
      {riskData?.crossAsset && (
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {/* Not "Cross-Asset ..." — that label belongs to the tenant-wide
                  Analytics → Cross-Asset Risk tab (concentration/VaR); this card
                  is the per-portfolio F-DD-001 sensitivity decomposition. */}
              <Layers className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">Sensitivities by Asset Class</h3>
            </div>
            <span className={cn(
              "px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border",
              riskData.crossAsset.rateDirection === 'NET_SHORT_RATES'
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                : riskData.crossAsset.rateDirection === 'NET_LONG_RATES'
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                  : "bg-blue-500/10 text-blue-400 border-blue-500/20"
            )}>
              {riskData.crossAsset.rateDirection.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {riskData.crossAsset.components.map((c, i) => (
              <div key={i} className="p-4 bg-slate-950/30 border border-white/5 rounded-2xl">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{c.assetClass}</span>
                  <span className="text-[10px] text-slate-600">{c.positions} pos</span>
                </div>
                <div className={cn("font-mono font-bold text-lg",
                  c.dv01 != null && c.dv01 > 0 ? "text-emerald-400" : c.dv01 != null && c.dv01 < 0 ? "text-red-400" : "text-slate-400")}>
                  {c.dv01 != null ? `${fmtSignedDollars(c.dv01)}/bp` : '—'}
                </div>
                <div className="text-xs text-slate-500 mt-1.5 space-y-0.5">
                  {c.assetClass === 'BONDS' && (
                    <>
                      <div>MV {fmtM(c.marketValue ?? NaN)}{c.duration != null ? ` · dur ${c.duration.toFixed(2)}y` : ''}</div>
                      {c.cs01 != null && (
                        <div>CS01 {fmtSignedDollars(c.cs01)}/bp</div>
                      )}
                    </>
                  )}
                  {c.assetClass === 'IRS' && (
                    <>
                      <div>Notional {fmtM(c.notional ?? NaN)} · NPV {fmtM(c.netPv ?? NaN)}</div>
                      {!!c.dv01MissingPositions && (
                        <div className="text-amber-400">{c.dv01MissingPositions} swap(s) missing DV01</div>
                      )}
                    </>
                  )}
                  {(c.assetClass === 'CDS' || c.assetClass === 'OTHER_SWAPS') && (
                    <>
                      <div>Notional {fmtM(c.notional ?? NaN)} · MTM {fmtM(c.netPv ?? NaN)}</div>
                      {c.cs01 != null && (
                        <div>CS01 {fmtSignedDollars(c.cs01)}/bp</div>
                      )}
                      {!!c.dv01MissingPositions && (
                        <div className="text-amber-400">
                          {c.assetClass === 'CDS'
                            ? `rate DV01 not modeled (${c.dv01MissingPositions} pos)`
                            : `${c.dv01MissingPositions} swap(s) missing DV01`}
                        </div>
                      )}
                    </>
                  )}
                  {c.assetClass === 'OPTIONS' && (
                    <>
                      <div>Δ-adj {fmtM(c.deltaAdjustedExposure ?? NaN)}</div>
                      <div>vega {fmtK(c.vega ?? 0)} · theta {fmtK(c.theta ?? 0)}/d</div>
                    </>
                  )}
                  {c.assetClass === 'FUTURES' && (
                    <div>β-adj {fmtM(c.betaAdjustedExposure ?? NaN)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              Equity exposure (Δ-adj + β-adj): <span className="font-mono font-bold text-slate-300">{fmtM(riskData.crossAsset.equityExposure.total)}</span>
            </span>
            <span className="italic">{riskData.crossAsset.convention}</span>
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <GlassCard className="xl:col-span-2 p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Flame className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-bold text-white">Stress Test Scenarios</h3>
            </div>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">PnL Sensitivity</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="pb-4 px-2">Scenario</th>
                  <th className="pb-4 px-2">PnL Impact</th>
                  <th className="pb-4 px-2 text-right">Severity</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {riskData?.stressScenarios?.map((s, i) => (
                  <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                    <td className="py-4 px-2">
                      <div className="font-bold text-white flex items-center gap-2">
                        <span>{s.icon}</span>
                        {s.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>
                    </td>
                    <td className={cn("py-4 px-2 font-mono font-bold", s.isNegative ? "text-red-400" : "text-emerald-400")}>
                      {s.pnlImpactFormatted}
                    </td>
                    <td className="py-4 px-2 text-right">
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                        s.severity === 'Severe' ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                        s.severity === 'High' ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                        "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      )}>
                        {s.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        <GlassCard className="p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-8">
            <Target className="w-5 h-5 text-indigo-400" />
            <h3 className="text-lg font-bold text-white">Risk Indicators</h3>
          </div>
          <div className="space-y-4 flex-1">
            {riskData?.keyRiskIndicators?.map((kri, i) => (
              <div key={i} className="p-4 bg-slate-950/30 border border-white/5 rounded-2xl">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-bold text-slate-300">{kri.name}</span>
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    kri.status === 'danger' ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" :
                    kri.status === 'warning' ? "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" :
                    "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                  )} />
                </div>
                <div className="text-lg font-bold text-white mb-1">{kri.value}</div>
                <div className="text-[10px] text-slate-500 font-medium leading-relaxed">{kri.description}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <Dna className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-bold text-white">Quality Distribution</h3>
          </div>
          <div className="flex items-center gap-12">
            <div className="relative w-48 h-48 flex-shrink-0">
              <div
                className="w-full h-full rounded-full"
                style={{
                  background: getDonutGradient(),
                  maskImage: 'radial-gradient(transparent 60%, black 61%)',
                  WebkitMaskImage: 'radial-gradient(transparent 60%, black 61%)'
                }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-2xl font-bold text-white">{(riskData?.qualityDistribution?.investmentGradePercent || 0).toFixed(0)}%</div>
                <div className="text-[10px] font-bold text-slate-500 uppercase">IG Rating</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4 flex-1">
              {riskData?.qualityDistribution?.distribution?.filter(d => d.percentage > 0).map((d, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: d.color }} />
                    <span className="text-xs font-bold text-slate-400">{d.rating}</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-white">{d.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <Layers className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-bold text-white">Concentration Risk</h3>
          </div>
          <div className="space-y-6">
            {riskData?.concentration?.byAssetClass?.slice(0, 4).map((item, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between items-end text-xs font-bold">
                  <span className="text-slate-400 uppercase tracking-widest">{item.category}</span>
                  <span className="text-white">{item.percentage.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    className="h-full bg-blue-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </>
  );

  const renderSleeveContent = () => {
    switch (activeSleeve) {
      case 'all':
        return (
          <>
            {renderAllAssetsSummary()}
            {renderExistingFIContent()}
          </>
        );
      case 'public-fi':
        return renderExistingFIContent();
      case 'private-credit':
        return renderPrivateCreditSection();
      case 'abf':
        return renderAbfSection();
      case 'real-estate':
        return renderRealEstateSection();
      case 'alternatives':
        return renderAlternativesSection();
      case 'insurance':
        return renderInsuranceSection();
      default:
        return renderExistingFIContent();
    }
  };

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <ShieldAlert className="w-8 h-8 text-blue-500" />
                Risk Surveillance
              </h1>
              <p className="text-slate-500 font-medium mt-1">Real-time telemetry across market, credit, and liquidity risk factors</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1.5 rounded-xl">
                <select
                  value={selectedPortfolioId || ''}
                  onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
                  className="bg-transparent text-xs font-bold text-slate-300 px-3 focus:outline-none cursor-pointer"
                >
                  {portfolios.map(p => <option key={p.id} value={p.id} className="bg-slate-900">{p.portfolioName}</option>)}
                </select>
                <div className="w-px h-4 bg-white/10 mx-2 self-center" />
                <DateField compact
                  value={asOfDate ?? ''}
                  onChange={iso => setAsOfDate(iso)}
                />
              </div>
              <button
                onClick={fetchRiskData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
                Refresh
              </button>
              <button
                onClick={generateAiCommentary}
                disabled={aiLoading || !riskData}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest"
              >
                {aiLoading ? <Bot className="w-4 h-4 animate-bounce" /> : <Sparkles className="w-4 h-4" />}
                Risk Insights
              </button>
            </div>
          </div>

          {/* Asset Class Sleeve Tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {RISK_SLEEVES.map((sleeve) => (
              <button
                key={sleeve.id}
                onClick={() => setActiveSleeve(sleeve.id)}
                className={cn(
                  "px-4 py-1.5 rounded-lg text-xs font-bold transition-all",
                  activeSleeve === sleeve.id
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                    : "bg-slate-900 border border-white/10 text-slate-500 hover:text-slate-300"
                )}
              >
                {sleeve.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-300" />
              <span className="flex-1">{error}</span>
              {/* Sanctioned producer path: recalculate on demand, then re-read.
                  Reads never calculate — this button is how a fresh portfolio
                  gets its first snapshot before EOD/posting produces one. */}
              <button
                onClick={handleRecalculateRisk}
                disabled={recalculating}
                className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
              >
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          )}

          {(() => {
            // #72: the staleness banner must describe the surface the ACTIVE
            // sleeve renders — the PC/LL feed has its own snapshot store and
            // status, independent of the main risk surface.
            const pcSurface = activeSleeve === 'private-credit';
            const status = pcSurface ? pcLlRiskData?.snapshotStatus : riskData?.riskSnapshotStatus;
            if (status !== 'LATEST_AVAILABLE') return null;
            const snapType = pcSurface ? pcLlRiskData?.snapshotType : riskData?.riskSnapshotType;
            const snapDate = pcSurface ? pcLlRiskData?.snapshotDate : riskData?.riskSnapshotDate;
            const requested = pcSurface ? pcLlRiskData?.requestedAsOfDate : riskData?.requestedAsOfDate;
            return (
              <div className="flex items-center gap-3 rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-sm font-semibold text-cyan-100">
                <Clock className="h-4 w-4 text-cyan-300" />
                <span className="flex-1">
                  Showing latest persisted {snapType || 'risk'} snapshot from {snapDate}
                  {requested ? ` for requested date ${requested}` : ''}.
                </span>
                {/* #68: a stale snapshot must be actionable, not just labeled. */}
                <button
                  onClick={handleRecalculateRisk}
                  disabled={recalculating}
                  className="rounded-lg border border-cyan-300/40 bg-cyan-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-cyan-100 transition-colors hover:bg-cyan-400/30 disabled:opacity-50"
                >
                  {recalculating ? 'Recalculating…' : 'Recalculate'}
                </button>
              </div>
            );
          })()}

          {/* KPI strip.
              - PE / Alts: suppressed here (the sleeve renders its own strip).
              - Private Credit: the generic rate-VaR / effective-duration feed is the
                wrong model for a floating-rate credit book (duration ~0 ⇒ DV01/VaR
                collapse to $0, and a signed net MV can read negative). When the
                native PC/LL credit feed is available we drive the strip from it
                (Spread DV01 / Base-rate DV01 / EAD / Economic Capital) and surface
                rate-VaR as n/a rather than a misleading $0.
              - Everything else: the standard FI strip (Exposure/Duration/VaR/DV01). */}
          {activeSleeve === 'private-credit' ? (
            pcLlRiskData?.summary ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard label="Total Exposure" value={fmtM(pcLlRiskData.summary.totalExposureAtDefault)} icon={<Activity className="w-5 h-5" />} color="#3b82f6" subText={`${pcLlRiskData.summary.positionCount || 0} loans · EAD`} />
                <StatCard label="Spread DV01" value={fmtK(pcLlRiskData.summary.spreadDv01)} icon={<TrendingDown className="w-5 h-5" />} color="#ec4899" subText={`Base-rate DV01: ${fmtK(pcLlRiskData.summary.baseRateDv01)}`} />
                <StatCard label="1D VaR (99%)" value="n/a" icon={<AlertTriangle className="w-5 h-5" />} color="#64748b" subText="Parametric rate-VaR not modeled for credit" />
                <StatCard label="Economic Capital" value={fmtM(pcLlRiskData.summary.totalEconomicCapital)} icon={<Activity className="w-5 h-5" />} color="#f59e0b" subText={`${fmtPctDec(pcLlRiskData.summary.economicCapitalPctOfFundedExposure)} of funded`} />
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
                <span className="flex-1">{pcLlRiskData?.error || 'PC/LL risk snapshot is not available for this date.'}</span>
                {/* #68: MISSING_SNAPSHOT is a producer problem — offer the producer. */}
                <button
                  onClick={handleRecalculateRisk}
                  disabled={recalculating}
                  className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
                >
                  {recalculating ? 'Recalculating…' : 'Recalculate'}
                </button>
              </div>
            )
          ) : (activeSleeve === 'all' || activeSleeve === 'public-fi') && riskData && (
            // F-PC-RT-042: the portfolio-level FI strip (Exposure/Duration/VaR/DV01)
            // is only meaningful for All-Assets and Public-FI. On Real-Estate /
            // Insurance / PE-Alts tabs it mis-labeled the whole-portfolio numbers as
            // that asset class; those tabs render their own scoped sections below.
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard label="Total Exposure" value={`$${((riskData?.totalMarketValue || 0) / 1000000).toFixed(1)}M`} icon={<Activity className="w-5 h-5" />} color="#3b82f6" subText={`${riskData?.positionCount || 0} positions`} />
              <StatCard label="Eff. Duration" value={`${(riskData?.duration || 0).toFixed(2)}y`} icon={<Clock className="w-5 h-5" />} color="#8b5cf6" subText={`Gap: ${((riskData?.duration || 0) - (riskData?.targetDuration || 0)).toFixed(2)}y`} />
              <StatCard label="1D VaR (99%)" value={`$${((riskData?.var?.var1Day99 || 0) / 1000).toFixed(1)}K`} icon={<AlertTriangle className="w-5 h-5" />} color="#f59e0b" subText={`${(((riskData?.var?.var1Day99 || 0) / (riskData?.totalMarketValue || 1)) * 100).toFixed(2)}% of MV`} />
              <StatCard
                label={riskData?.netDv01 != null ? 'Net DV01' : 'DV01'}
                value={riskData?.netDv01 != null
                  ? `${fmtSignedDollars(riskData.netDv01)}/bp`
                  : `$${(riskData?.dv01 || 0).toLocaleString()}`}
                icon={<TrendingUp className="w-5 h-5" />}
                color="#10b981"
                subText={riskData?.grossDv01 != null
                  ? `Gross ${fmtSignedDollars(riskData.grossDv01).replace('+', '')}/bp · signed P&L per +1bp`
                  : 'Risk per 1bp move'}
              />
            </div>
          )}
        </motion.div>

        <AnimatePresence>
          {aiCommentary && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-6 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 text-indigo-400">
                  <Bot className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-widest text-white">AI Risk Commentary</span>
                </div>
                <button onClick={() => setAiCommentary(null)} className="text-slate-500 hover:text-white transition-colors">×</button>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed italic">{aiCommentary.commentary || aiCommentary.executiveSummary}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {renderSleeveContent()}
      </div>

      {/* Explain-every-number drawer. The Risk page has no per-loan P&L, so the
          attribution-only `loan` prop is null (the drawer renders the P&L card as a
          friendly "available in Return Attribution view" placeholder). The selected
          loan row IS the risk row consumed by Cards 1–3 + Economic Capital + sources. */}
      <LoanExplainDrawer
        loan={null}
        risk={selectedRiskLoan ? (selectedRiskLoan as unknown as LoanRiskRow) : null}
        onClose={() => setSelectedRiskLoan(null)}
      />
    </Container>
  );
};

export default RiskDashboardScreen;
